import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy, input, output, inject } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

// PrimeNG imports
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TagModule } from 'primeng/tag';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { TooltipModule } from 'primeng/tooltip';

// Models and services
import { ToastMessageService } from '@fitness-force/api';
import { ConfirmationService } from 'primeng/api';

// Import sub-components
import { CategoryFormComponent } from '../category-form/category-form.component';
import { ListProductCategoriesResponse, ProductCategoryResponse } from '../../models';
import { ProductCategoryService } from '../../services';
import { Store } from '@ngrx/store';
import type { TENANTSINTERFACE } from '@fitness-force/models';
import { TENANTS_INFO } from '@fitness-force/state';

@Component({
  selector: 'lib-category-list',
  standalone: true,
  imports: [
    FormsModule,
    TranslateModule,
    DialogModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    ToastModule,
    ConfirmDialogModule,
    TagModule,
    IconFieldModule,
    InputIconModule,
    TooltipModule,
    CategoryFormComponent
],
  providers: [ConfirmationService],
  template: `
    <p-dialog
      [visible]="visible()"
      [modal]="true"
      [closable]="true"
      [draggable]="false"
      [resizable]="false"
      [style]="{ width: '900px', height: '80vh' }"
      (onHide)="close()">
    
      <ng-template pTemplate="header">
        <h3>{{ 'RETAIL_BILLING.CATEGORY.MANAGE' | translate }}</h3>
      </ng-template>
    
      <div class="card">
        <div class="flex justify-content-between align-items-center mb-4">
          <p-iconField iconPosition="left">
            <p-inputIcon styleClass="pi pi-search" />
            <input
              pInputText
              type="text"
              [placeholder]="'common.PLACEHOLDER.SEARCH' | translate"
              [(ngModel)]="searchTerm"
              (ngModelChange)="onSearch()"
              aria-label="search-categories-input" />
            </p-iconField>
    
            <p-button [label]="'RETAIL_BILLING.CATEGORY.CREATE' | translate"
              icon="pi pi-plus"
              (onClick)="showCreateDialog()"
              aria-label="create-category-button" />
          </div>
    
          <p-table
            [value]="categories"
            [loading]="loading"
            [paginator]="true"
            [lazy]="true"
            [rows]="pageSize"
            [(first)]="first"
            [totalRecords]="totalRecords"
            (onPage)="onPageChange($event)"
            [showCurrentPageReport]="true"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
            [rowsPerPageOptions]="[10, 25, 50]"
            paginatorDropdownAppendTo="body"
            styleClass="p-datatable-gridlines">
    
            <ng-template pTemplate="header">
              <tr>
                <th>{{ 'RETAIL_BILLING.CATEGORY.NAME' | translate }}</th>
                <th>{{ 'RETAIL_BILLING.CATEGORY.DESCRIPTION' | translate }}</th>
                <th>{{ 'RETAIL_BILLING.PRODUCTS.STATUS' | translate }}</th>
                <th>{{ 'RETAIL_BILLING.COMMON.ACTIONS' | translate }}</th>
              </tr>
            </ng-template>
    
            <ng-template pTemplate="body" let-category>
              <tr>
                <td>
                  <div class="flex align-items-center">
                    @if (category.image_url) {
                      <img
                        [src]="category.image_url"
                        [alt]="category.name_primary_language"
                        class="w-3rem h-3rem mr-2 border-round" />
                    }
                    <div>
                      <div class="font-medium">{{ category.name_primary_language }}</div>
                      <div class="text-sm text-500">{{ category.name_secondary_language }}</div>
                    </div>
                  </div>
                </td>
                <td>
                  <div>
                    <div class="font-medium">{{ category.description_primary_language }}</div>
                    <div class="text-sm text-500">{{ category.description_secondary_language }}</div>
                  </div>
                </td>
                <td>
                  <p-tag [value]="category.is_active ? ('RETAIL_BILLING.PRODUCTS.ACTIVE' | translate) : ('RETAIL_BILLING.PRODUCTS.INACTIVE' | translate)"
                    [severity]="category.is_active ? 'success' : 'danger'" />
                </td>
                <td>
                  <div class="flex gap-2">
                    <p-button [icon]="category.is_active ? 'pi pi-check-circle' : 'pi pi-times-circle'"
                      [pTooltip]="category.is_active ? ('RETAIL_BILLING.COMMON.MARK_INACTIVE' | translate) : ('RETAIL_BILLING.COMMON.MARK_ACTIVE' | translate)"
                      tooltipPosition="top"
                      [text]="true"
                      [rounded]="true"
                      [outlined]="true"
                      size="small"
                      [severity]="category.is_active ? 'success' : 'danger'"
                      (onClick)="toggleCategoryStatus(category)"
                      [ariaLabel]="'toggle-status-category-' + category.id" />
                    <p-button icon="pi pi-pencil"
                      [pTooltip]="'RETAIL_BILLING.COMMON.EDIT' | translate"
                      tooltipPosition="top"
                      [text]="true"
                      [rounded]="true"
                      [outlined]="true"
                      size="small"
                      (onClick)="editCategory(category)"
                      [ariaLabel]="'edit-category-' + category.id" />
                  </div>
                </td>
              </tr>
            </ng-template>
    
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="4" class="text-center">{{ 'RETAIL_BILLING.CATEGORY.NO_CATEGORIES' | translate }}</td>
              </tr>
            </ng-template>
          </p-table>
        </div>
    
        <ng-template pTemplate="footer">
          <div class="flex justify-content-end">
            <p-button [label]="'RETAIL_BILLING.COMMON.CLOSE' | translate"
              icon="pi pi-times"
              [text]="true"
              (onClick)="close()"
              aria-label="close-button" />
          </div>
        </ng-template>
      </p-dialog>
    
      @if (showCategoryDialog) {
        <lib-category-form [visible]="showCategoryDialog"
          [category]="selectedCategory"
          [companyLevel]="companyLevel()"
          [tenantId]="tenantId()"
          [primaryLanguage]="primaryLanguage()"
          [secondaryLanguage]="secondaryLanguage()"
          [isSecondaryLanguage]="isSecondaryLanguage()"
          (onSave)="onCategorySaved($event)"
          (onCancel)="hideCategoryDialog()" />
      }
    
      <p-confirmDialog />
      <p-toast />
    `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CategoryListComponent implements OnInit, OnDestroy {
  private readonly categoryService = inject(ProductCategoryService);
  private readonly toastService = inject(ToastMessageService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly translate = inject(TranslateService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly store = inject(Store);

  readonly visible = input(false);
  readonly companyLevel = input<boolean>(true);
  readonly tenantId = input<number>();
  readonly primaryLanguage = input<string>('English');
  readonly secondaryLanguage = input<string>('Arabic');
  readonly isSecondaryLanguage = input<boolean>(false);
  readonly visibleChange = output<boolean>();
  readonly onClose = output<void>();

  categories: ProductCategoryResponse[] = [];
  loading = false;
  totalRecords = 0;
  pageSize = 10;
  pageNumber = 1;
  searchTerm = '';
  first = 0;

  showCategoryDialog = false;
  selectedCategory: ProductCategoryResponse | null = null;
  private readonly destroy$ = new Subject<void>();
  private readonly searchSubject$ = new Subject<string>();

  ngOnInit() {
    this.searchSubject$
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(() => {
        this.pageNumber = 1;
        this.first = 0;
        this.loadCategories();
      });

    this.store.select(TENANTS_INFO)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (tenant: TENANTSINTERFACE) => {
          if (tenant.selected_tenant.id > 0) {
            this.store.dispatch({ type: 'ROUTER_GO' });
            this.loadCategories();
          }
        }
      });
  }

  loadCategories() {
    this.loading = true;

    this.categoryService.getCompanyCategories({
      page: this.pageNumber,
      page_size: this.pageSize,
      search_term: this.searchTerm || undefined,
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: ListProductCategoriesResponse) => {
          this.categories = response.items || [];
          this.totalRecords = response.total_items;
          if (response.page_size) {
            this.pageSize = response.page_size;
          }
          if (response.current_page) {
            this.pageNumber = response.current_page;
            this.first = (response.current_page - 1) * this.pageSize;
          }
          this.loading = false;
          this.changeDetectorRef.detectChanges();
        },
        error: (error) => {
          this.toastService.showError(error);
          this.loading = false;
          this.changeDetectorRef.detectChanges();
        }
      });
  }

  onSearch() {
    this.searchSubject$.next(this.searchTerm);
  }

  onPageChange(e: { first: number; rows: number }) {
    this.pageSize = e.rows;
    this.first = e.first;
    this.pageNumber = e.first ? e.first / this.pageSize + 1 : 1;
    this.loadCategories();
  }

  showCreateDialog() {
    this.selectedCategory = null;
    this.showCategoryDialog = true;
  }

  editCategory(category: ProductCategoryResponse) {
    this.selectedCategory = category;
    this.showCategoryDialog = true;
  }

  toggleCategoryStatus(category: ProductCategoryResponse) {
    const newStatus = !category.is_active;
    const confirmMessage = newStatus 
      ? this.translate.instant('RETAIL_BILLING.COMMON.CONFIRM_ACTIVATE')
      : this.translate.instant('RETAIL_BILLING.COMMON.CONFIRM_DEACTIVATE');
    
    this.confirmationService.confirm({
      message: confirmMessage,
      header: this.translate.instant('RETAIL_BILLING.COMMON.CONFIRM'),
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        const updatedCategory = {
          ...category,
          is_active: newStatus
        };
        
        this.categoryService.updateCompanyCategory(category.id, updatedCategory)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              const successMessage = newStatus
                ? this.translate.instant('RETAIL_BILLING.CATEGORY.ACTIVATED_SUCCESS')
                : this.translate.instant('RETAIL_BILLING.CATEGORY.DEACTIVATED_SUCCESS');
              this.toastService.showSuccess(successMessage);
              this.loadCategories();
            },
            error: (error) => {
              this.toastService.showError(error);
            }
          });
      }
    });
  }

  onCategorySaved(category: ProductCategoryResponse) {
    this.hideCategoryDialog();
    this.loadCategories();
    const message = this.selectedCategory 
      ? 'RETAIL_BILLING.CATEGORY.UPDATE_SUCCESS'
      : 'RETAIL_BILLING.CATEGORY.CREATE_SUCCESS';
    this.toastService.showSuccess(this.translate.instant(message));
  }

  hideCategoryDialog() {
    this.showCategoryDialog = false;
    this.selectedCategory = null;
  }

  close(): void {
    this.onClose.emit();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.visibleChange.emit(false);
  }
}
